/**
 * Authentication API Service
 * Handles login, registration, logout, and token management
 */

import apiClient, { ApiError, getErrorMessage } from './client';

// Types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  confirmPassword?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  theme: 'light' | 'dark';
  language: 'en' | 'es' | 'sw';
  subscription?: {
    tier: string;
    status: string;
    currentPeriodEnd: string;
  };
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: User;
    token: string;
    refreshToken: string;
  };
  message?: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  password: string;
  confirmPassword: string;
}

/**
 * Authentication Service
 */
export const authService = {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
      
      // Save tokens and user to localStorage
      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500,
        error.response?.data?.code
      );
    }
  },

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register', data);
      
      // Save tokens and user to localStorage
      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500,
        error.response?.data?.code
      );
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API response
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    const user = this.getCurrentUser();
    return !!(token && user);
  },

  /**
   * Get auth token
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  },

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post<{ success: boolean; data: { token: string } }>(
        '/auth/refresh',
        { refreshToken }
      );
      
      const { token } = response.data.data;
      localStorage.setItem('auth_token', token);
      
      return token;
    } catch (error: any) {
      // Clear auth data on refresh failure
      this.logout();
      throw new ApiError(
        'Session expired. Please login again.',
        401
      );
    }
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(data: PasswordResetRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/auth/forgot-password', data);
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500
      );
    }
  },

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(data: PasswordResetConfirm): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/auth/reset-password', data);
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500
      );
    }
  },

  /**
   * Update user preferences
   */
  async updatePreferences(preferences: { theme?: 'light' | 'dark'; language?: 'en' | 'es' | 'sw' }): Promise<User> {
    try {
      const response = await apiClient.patch<{ success: boolean; data: User }>('/users/preferences', preferences);
      
      // Update user in localStorage
      const user = response.data.data;
      localStorage.setItem('user', JSON.stringify(user));
      
      return user;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500
      );
    }
  },

  /**
   * Get user profile
   */
  async getProfile(): Promise<User> {
    try {
      const response = await apiClient.get<{ success: boolean; data: User }>('/users/profile');
      
      // Update user in localStorage
      const user = response.data.data;
      localStorage.setItem('user', JSON.stringify(user));
      
      return user;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500
      );
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    try {
      const response = await apiClient.patch<{ success: boolean; data: User }>('/users/profile', data);
      
      // Update user in localStorage
      const user = response.data.data;
      localStorage.setItem('user', JSON.stringify(user));
      
      return user;
    } catch (error: any) {
      throw new ApiError(
        getErrorMessage(error),
        error.response?.status || 500
      );
    }
  },
};

export default authService;
